import React from "react";
import { api, ASSET_URL } from "../../lib/api";
import { toast } from "sonner";
import { Save, Trash2, Upload, FileText, X } from "lucide-react";

const empty = {
  title_de: "", title_en: "", medium: "", date: "", summary_de: "", summary_en: "",
  url: "", pdf_media_id: null, image_media_id: null, order: 0, enabled: true,
};

export default function PressAdmin() {
  const [items, setItems] = React.useState([]);
  const [draft, setDraft] = React.useState({ ...empty });
  const [editId, setEditId] = React.useState(null);
  const [pdfAsset, setPdfAsset] = React.useState(null);
  const [imgAsset, setImgAsset] = React.useState(null);
  const [uploading, setUploading] = React.useState(false);
  const [imgUploading, setImgUploading] = React.useState(false);
  const fileRef = React.useRef(null);
  const imgRef = React.useRef(null);

  const load = () => api.adminPress().then(setItems);
  React.useEffect(() => { load(); }, []);

  const resolveAssets = async (pdfId, imgId) => {
    const all = (pdfId || imgId) ? await api.adminMedia() : [];
    setPdfAsset(pdfId ? all.find(m => m.id === pdfId) || null : null);
    setImgAsset(imgId ? all.find(m => m.id === imgId) || null : null);
  };

  const save = async () => {
    try {
      if (editId) await api.updatePress(editId, draft);
      else await api.createPress(draft);
      toast.success(draft.url && !draft.image_media_id && !draft.pdf_media_id ? "Gespeichert — Vorschaubild wird im Hintergrund geladen" : "Gespeichert");
      setDraft({ ...empty }); setEditId(null); setPdfAsset(null); setImgAsset(null); load();
    } catch { toast.error("Fehler beim Speichern"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Diesen Presse-Eintrag löschen?")) return;
    await api.deletePress(id); load();
  };
  const edit = (it) => { setDraft({ ...empty, ...it }); setEditId(it.id); resolveAssets(it.pdf_media_id, it.image_media_id); };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) { toast.error("Bitte eine PDF-Datei wählen"); return; }
    setUploading(true);
    try {
      const asset = await api.uploadMedia(file, "document");
      setDraft(d => ({ ...d, pdf_media_id: asset.id }));
      setPdfAsset(asset);
      toast.success("PDF hochgeladen — Vorschau erzeugt");
    } catch { toast.error("Upload fehlgeschlagen"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const onUploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const name = file.name.toLowerCase();
    const isPdf = name.endsWith(".pdf");
    const isImg = /\.(jpe?g|png|webp|gif)$/.test(name);
    if (!isPdf && !isImg) { toast.error("Bitte ein Bild (JPG/PNG) oder PDF wählen"); return; }
    setImgUploading(true);
    try {
      const asset = await api.uploadMedia(file, isPdf ? "document" : "image");
      setDraft(d => ({ ...d, image_media_id: asset.id }));
      setImgAsset(asset);
      toast.success("Vorschaubild gesetzt (in WebP umgewandelt)");
    } catch { toast.error("Upload fehlgeschlagen"); }
    finally { setImgUploading(false); if (imgRef.current) imgRef.current.value = ""; }
  };

  const clearPdf = () => { setDraft(d => ({ ...d, pdf_media_id: null })); setPdfAsset(null); };
  const clearImg = () => { setDraft(d => ({ ...d, image_media_id: null })); setImgAsset(null); };

  return (
    <div data-testid="admin-press">
      <div className="mb-6"><div className="brut-tag mb-3">CMS / PRESSE</div><h1 className="brut-h2">Presse</h1></div>
      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 md:col-span-7 space-y-3">
          {items.map(it => (
            <div key={it.id} className="border-2 border-[var(--ink)] p-4" data-testid={`press-admin-item-${it.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-mono text-[10px] tracking-[0.2em] uppercase opacity-60">{it.medium} · {it.date} {it.enabled ? "" : "· (versteckt)"}</div>
                  <div className="brut-h3 mt-1">{it.title_de}</div>
                  {it.summary_de && <p className="brut-body opacity-80 mt-2 text-sm">{it.summary_de}</p>}
                  <div className="flex gap-4 mt-2 text-[11px] font-mono uppercase opacity-70">
                    {it.url && <span>↗ Link</span>}
                    {it.pdf_media_id && <span>📄 PDF</span>}
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button onClick={() => edit(it)} className="font-mono text-xs underline" data-testid={`press-edit-${it.id}`}>Edit</button>
                  <button onClick={() => remove(it.id)} className="font-mono text-xs underline text-red-600" data-testid={`press-delete-${it.id}`}><Trash2 size={12} /></button>
                </div>
              </div>
            </div>
          ))}
          {items.length === 0 && <p className="brut-body opacity-60">Noch keine Presse-Einträge.</p>}
        </div>

        <div className="col-span-12 md:col-span-5">
          <div className="border-2 border-[var(--ink)] p-4 space-y-3 sticky top-6">
            <div className="font-mono text-xs uppercase tracking-[0.2em]">{editId ? "Eintrag bearbeiten" : "Neuer Eintrag"}</div>
            <input className="brut-input" placeholder="Titel (DE)" value={draft.title_de} onChange={e => setDraft({ ...draft, title_de: e.target.value })} data-testid="press-title-de" />
            <input className="brut-input" placeholder="Title (EN)" value={draft.title_en || ""} onChange={e => setDraft({ ...draft, title_en: e.target.value })} />
            <div className="grid grid-cols-2 gap-3">
              <input className="brut-input" placeholder="Medium (z.B. ZM Online)" value={draft.medium} onChange={e => setDraft({ ...draft, medium: e.target.value })} data-testid="press-medium" />
              <input className="brut-input" type="date" value={draft.date || ""} onChange={e => setDraft({ ...draft, date: e.target.value })} data-testid="press-date" />
            </div>
            <textarea rows={3} className="brut-textarea" placeholder="Kurztext (DE)" value={draft.summary_de} onChange={e => setDraft({ ...draft, summary_de: e.target.value })} />
            <textarea rows={2} className="brut-textarea" placeholder="Summary (EN)" value={draft.summary_en || ""} onChange={e => setDraft({ ...draft, summary_en: e.target.value })} />
            <input className="brut-input" placeholder="Externer Link (URL, optional)" value={draft.url || ""} onChange={e => setDraft({ ...draft, url: e.target.value })} data-testid="press-url" />

            <div className="border border-[var(--line-soft)] p-3 space-y-2">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] opacity-60">PDF-Dokument (optional)</div>
              {pdfAsset ? (
                <div className="flex items-center gap-3">
                  {(pdfAsset.variants?.thumb || pdfAsset.variants?.preview)
                    ? <img src={ASSET_URL(pdfAsset.variants.thumb || pdfAsset.variants.preview)} alt="PDF Vorschau" className="w-16 h-20 object-cover border border-[var(--ink)]" />
                    : <FileText size={28} />}
                  <div className="flex-1 min-w-0">
                    <div className="text-xs truncate">{pdfAsset.original_filename || "Dokument"}</div>
                    <button onClick={clearPdf} className="font-mono text-[11px] underline text-red-600 inline-flex items-center gap-1 mt-1"><X size={11} /> Entfernen</button>
                  </div>
                </div>
              ) : (
                <button onClick={() => fileRef.current?.click()} disabled={uploading} className="brut-btn !py-2 !px-3 text-xs" data-testid="press-upload-pdf">
                  <Upload size={12} /> {uploading ? "Lädt…" : "PDF hochladen"}
                </button>
              )}
              <input ref={fileRef} type="file" accept="application/pdf,.pdf" className="hidden" onChange={onUpload} data-testid="press-pdf-input" />
            </div>

            <div className="border border-[var(--line-soft)] p-3 space-y-2">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] opacity-60">Vorschaubild (optional)</div>
              {imgAsset ? (
                <div className="flex items-center gap-3">
                  <img src={ASSET_URL(imgAsset.variants?.thumb || imgAsset.variants?.webp || imgAsset.variants?.preview || imgAsset.variants?.original)} alt="Vorschau" className="w-20 h-16 object-cover border border-[var(--ink)]" />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs truncate">{imgAsset.original_filename || "Bild"}</div>
                    <button onClick={clearImg} className="font-mono text-[11px] underline text-red-600 inline-flex items-center gap-1 mt-1"><X size={11} /> Entfernen</button>
                  </div>
                </div>
              ) : (
                <button onClick={() => imgRef.current?.click()} disabled={imgUploading} className="brut-btn !py-2 !px-3 text-xs" data-testid="press-upload-image">
                  <Upload size={12} /> {imgUploading ? "Lädt…" : "Bild/PDF hochladen"}
                </button>
              )}
              <p className="text-[10px] opacity-60 leading-snug">JPG/PNG/PDF — wird automatisch in WebP umgewandelt. Bei einem externen Link wird sonst automatisch das Artikel-Vorschaubild (OG-Image) geladen.</p>
              <input ref={imgRef} type="file" accept="image/*,application/pdf,.pdf" className="hidden" onChange={onUploadImage} data-testid="press-image-input" />
            </div>

            <div className="grid grid-cols-2 gap-3 items-center">
              <input className="brut-input" type="number" placeholder="Reihenfolge" value={draft.order} onChange={e => setDraft({ ...draft, order: parseInt(e.target.value) || 0 })} />
              <label className="flex items-center gap-2 text-xs font-mono uppercase">
                <input type="checkbox" checked={draft.enabled} onChange={e => setDraft({ ...draft, enabled: e.target.checked })} /> Sichtbar
              </label>
            </div>
            <div className="flex gap-2">
              <button onClick={save} className="brut-btn !py-2 !px-3 text-xs" data-testid="press-save"><Save size={12} /> Speichern</button>
              {editId && <button onClick={() => { setDraft({ ...empty }); setEditId(null); setPdfAsset(null); }} className="font-mono text-xs underline">Abbrechen</button>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
